import type { CharLook } from './looks';
import { flipped, Px } from './px';

/** Спрайты из кода: персонаж 16×16 (четыре стороны, шаг), портрет 32×32 для окна диалога, босс 48×48, значки. */

export type Dir = 'down' | 'up' | 'left' | 'right';
export const OUTLINE = '#1b1a26';
const EYE = '#1b1a26';
const SHOE = '#2b2522';
const WHITE = '#ffffff';
const FRAME = '#2a2a35';

export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) =>
    Math.max(0, Math.min(255, Math.round(amount >= 0 ? v + (255 - v) * amount : v * (1 + amount)))),
  );
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function lookKey(look: CharLook): string {
  return [look.skin, look.hair, look.hairStyle, look.top, look.bottom, look.accent, look.extras.join('+')].join('|');
}

// ---------------------------------------------------------------------------------------------- персонаж 16×16
function headHair(px: Px, look: CharLook, dir: Dir): void {
  const { hair, accent, hairStyle } = look;
  const side = dir === 'left' || dir === 'right';
  const cx = side ? 7.5 : 8;
  const rx = side ? 4.5 : 5;
  if (dir === 'up') {
    if (hairStyle === 'bald') {
      px.ellipse(cx, 5, rx, 4, hair, (x, y) => (x <= 3 || x >= 12) && y >= 3 && y <= 6);
    } else {
      px.ellipse(cx, 5, rx, 4, hair, (_x, y) => y <= 7);
    }
    if (hairStyle === 'long') px.rect(4, 7, 8, 4, hair);
    if (hairStyle === 'bun') px.ellipse(8, 1.5, 1.6, 1.5, hair);
    if (hairStyle === 'curly') [3.5, 12.5].forEach((x) => px.ellipse(x, 4, 1.5, 1.6, hair));
    if (hairStyle === 'cap' || hairStyle === 'beanie') px.ellipse(cx, 4, rx, 3.4, accent, (_x, y) => y <= 4);
    if (hairStyle === 'beanie') px.set(8, 0, shade(accent, 0.3));
    return;
  }
  if (side) {
    // смотрит влево: волосы — затылок справа и макушка
    const back = (x: number, y: number) => x >= 7 || y <= 3;
    if (hairStyle === 'bald') px.ellipse(cx, 5, rx, 4, hair, (x, y) => x >= 9 && y >= 3 && y <= 5);
    else px.ellipse(cx, 5, rx, 4, hair, back);
    if (hairStyle === 'long') px.rect(8, 7, 3, 4, hair);
    if (hairStyle === 'bun') px.ellipse(10, 1.5, 1.6, 1.5, hair);
    if (hairStyle === 'curly') {
      px.ellipse(11.5, 5, 1.5, 2, hair);
      px.ellipse(9, 1.5, 2, 1.4, hair);
    }
    if (hairStyle === 'cap') {
      px.ellipse(cx, 4, rx, 3.4, accent, (_x, y) => y <= 3);
      px.rect(2, 4, 5, 1, shade(accent, -0.25));
    }
    if (hairStyle === 'beanie') {
      px.ellipse(cx, 3.5, rx + 0.3, 3.4, accent, (_x, y) => y <= 3);
      px.rect(3, 3, 9, 1, shade(accent, -0.25));
      px.set(8, 0, shade(accent, 0.3));
    }
    return;
  }
  // лицом к нам
  if (hairStyle === 'short') px.ellipse(cx, 5, rx, 4, hair, (x, y) => y <= 2 || ((x <= 3 || x >= 12) && y <= 5));
  if (hairStyle === 'long') {
    px.ellipse(cx, 5, rx, 4, hair, (x, y) => y <= 2 || x <= 4 || x >= 11);
    px.rect(3, 8, 2, 3, hair);
    px.rect(11, 8, 2, 3, hair);
  }
  if (hairStyle === 'bun') {
    px.ellipse(cx, 5, rx, 4, hair, (x, y) => y <= 2 || ((x <= 3 || x >= 12) && y <= 5));
    px.ellipse(8, 1.2, 1.7, 1.3, hair);
  }
  if (hairStyle === 'curly') {
    px.ellipse(cx, 5, rx, 4, hair, (x, y) => y <= 3 || ((x <= 3 || x >= 12) && y <= 6));
    [2.5, 13.5].forEach((x) => px.ellipse(x, 4, 1.4, 2, hair));
    [5, 11].forEach((x) => px.ellipse(x, 1.2, 1.8, 1.2, hair));
  }
  if (hairStyle === 'bald') px.ellipse(cx, 5, rx, 4, hair, (x, y) => (x <= 3 || x >= 12) && y >= 3 && y <= 5);
  if (hairStyle === 'cap') {
    px.ellipse(cx, 4, rx, 3.4, accent, (_x, y) => y <= 3);
    px.rect(4, 4, 8, 1, shade(accent, -0.25));
    px.set(3, 5, hair);
    px.set(12, 5, hair);
  }
  if (hairStyle === 'beanie') {
    px.ellipse(cx, 3.5, rx + 0.3, 3.4, accent, (_x, y) => y <= 3);
    px.rect(3, 3, 10, 1, shade(accent, -0.25));
    px.set(8, 0, shade(accent, 0.3));
    px.set(3, 4, hair);
    px.set(12, 4, hair);
  }
}

function charPx(look: CharLook, dir: Dir, frame: 0 | 1 | 2): Px {
  const px = new Px(16, 16);
  const side = dir === 'left' || dir === 'right';
  const has = (e: CharLook['extras'][number]) => look.extras.includes(e);
  const topDark = shade(look.top, -0.18);
  // ноги
  if (!side) {
    const lUp = frame === 1 ? 1 : 0;
    const rUp = frame === 2 ? 1 : 0;
    px.rect(5, 13 - lUp, 2, 1, look.bottom).rect(5, 14 - lUp, 2, 1, SHOE);
    px.rect(9, 13 - rUp, 2, 1, look.bottom).rect(9, 14 - rUp, 2, 1, SHOE);
  } else if (frame === 0) {
    px.rect(6, 13, 3, 1, look.bottom).rect(5, 14, 4, 1, SHOE);
  } else {
    const front = frame === 1 ? 4 : 5;
    const back = frame === 1 ? 8 : 7;
    px.rect(back, 13, 2, 1, shade(look.bottom, -0.2)).rect(back, 14, 2, 1, SHOE);
    px.rect(front, 13, 2, 1, look.bottom).rect(front - 1, 14, 3, 1, SHOE);
  }
  // тело и руки
  if (!side) {
    px.rect(4, 9, 8, 4, look.top).rect(4, 12, 8, 1, topDark);
    const lA = frame === 2 ? 1 : 0;
    const rA = frame === 1 ? 1 : 0;
    px.rect(3, 9 + lA, 1, 3, topDark).set(3, 12 + lA, look.skin);
    px.rect(12, 9 + rA, 1, 3, topDark).set(12, 12 + rA, look.skin);
    if (dir === 'down') {
      if (has('collar') && !has('apron')) px.set(6, 9, WHITE).set(9, 9, WHITE);
      if (has('tie')) px.rect(7, 9, 2, 3, look.accent);
      if (has('apron')) px.rect(5, 10, 6, 3, look.accent).set(6, 9, look.accent).set(9, 9, look.accent);
      if (has('backpack')) px.rect(5, 9, 1, 3, look.accent).rect(10, 9, 1, 3, look.accent);
    } else if (has('backpack')) {
      px.rect(5, 9, 6, 4, look.accent).rect(5, 9, 6, 1, shade(look.accent, -0.25));
    }
  } else {
    px.rect(5, 9, 6, 4, look.top).rect(5, 12, 6, 1, topDark);
    if (has('backpack')) px.rect(10, 9, 2, 4, look.accent);
    if (has('apron')) px.rect(5, 10, 3, 3, look.accent);
    const sw = frame === 1 ? -1 : frame === 2 ? 1 : 0;
    px.rect(7 + sw, 9, 2, 3, topDark).rect(7 + sw, 12, 2, 1, look.skin);
  }
  // голова
  px.ellipse(side ? 7.5 : 8, 5, side ? 4.5 : 5, 4, look.skin);
  headHair(px, look, dir);
  if (dir === 'down') {
    px.rect(5, 5, 1, 2, EYE).rect(10, 5, 1, 2, EYE);
    if (has('glasses')) px.rect(4, 4, 3, 1, FRAME).rect(9, 4, 3, 1, FRAME);
    if (has('beard')) px.ellipse(8, 6.5, 4, 2.4, look.hair, (_x, y) => y >= 7);
    if (has('headset')) px.set(3, 6, FRAME).set(4, 7, FRAME);
  } else if (side) {
    px.rect(4, 5, 1, 2, EYE);
    if (has('glasses')) px.rect(3, 4, 3, 1, FRAME);
    if (has('beard')) px.ellipse(6, 7, 3, 2, look.hair, (x, y) => y >= 7 && x <= 7);
    if (has('headset')) px.set(8, 5, FRAME).set(8, 6, FRAME);
  }
  return px.outline(OUTLINE);
}

const charCache = new Map<string, HTMLCanvasElement>();

/** Кадр персонажа: frame 0 — стоит, 1 и 2 — шаг. Вправо — зеркало кадра «влево». */
export function charSprite(look: CharLook, dir: Dir, frame: 0 | 1 | 2): HTMLCanvasElement {
  const key = `${lookKey(look)}|${dir}|${frame}`;
  let cv = charCache.get(key);
  if (!cv) {
    cv = dir === 'right' ? flipped(charPx(look, 'left', frame).canvas()) : charPx(look, dir, frame).canvas();
    charCache.set(key, cv);
  }
  return cv;
}

// ---------------------------------------------------------------------------------------------- портрет 32×32
function portraitPx(look: CharLook, mouthOpen: boolean, blink: boolean): Px {
  const px = new Px(32, 32);
  const has = (e: CharLook['extras'][number]) => look.extras.includes(e);
  const hairDark = shade(look.hair, -0.3);
  const skinDark = shade(look.skin, -0.16);
  const style = look.hairStyle;
  // волосы за спиной
  if (style === 'long') px.ellipse(16, 19, 11, 12, look.hair, (_x, y) => y >= 8 && y <= 29);
  // плечи, шея
  px.ellipse(16, 33.5, 14.5, 9.5, look.top);
  px.rect(13, 19, 6, 7, skinDark);
  if (has('collar')) px.line(12, 24, 15, 27, '#f4f2ee').line(19, 24, 16, 27, '#f4f2ee');
  if (has('tie')) px.rect(14, 25, 4, 2, shade(look.accent, -0.2)).rect(15, 27, 2, 5, look.accent);
  if (has('apron')) px.rect(10, 27, 12, 5, look.accent).line(11, 24, 11, 27, look.accent).line(20, 24, 20, 27, look.accent);
  if (has('backpack')) px.rect(8, 25, 2, 7, look.accent).rect(22, 25, 2, 7, look.accent);
  // голова и уши
  px.ellipse(7, 14, 1.6, 2.4, look.skin).ellipse(25, 14, 1.6, 2.4, look.skin);
  px.ellipse(16, 13, 8.5, 9.5, look.skin);
  // причёска
  const crown = (until: number) => px.ellipse(16, 10, 9.6, 7.6, look.hair, (_x, y) => y <= until);
  if (style === 'short') {
    crown(8);
    px.rect(10, 9, 5, 1, look.hair).rect(7, 9, 2, 5, look.hair).rect(23, 9, 2, 5, look.hair);
  }
  if (style === 'long') {
    crown(8);
    px.rect(7, 8, 3, 16, look.hair).rect(22, 8, 3, 16, look.hair).rect(11, 9, 6, 1, look.hair);
  }
  if (style === 'bun') {
    crown(8);
    px.ellipse(16, 2.6, 4, 2.8, look.hair).rect(7, 9, 2, 4, look.hair).rect(23, 9, 2, 4, look.hair);
  }
  if (style === 'curly') {
    px.ellipse(16, 9.5, 11, 8, look.hair, (_x, y) => y <= 9);
    [
      [5.5, 9],
      [26.5, 9],
      [8, 4.5],
      [24, 4.5],
      [16, 2.5],
      [12, 3],
      [20, 3],
    ].forEach(([x, y]) => px.ellipse(x, y, 2.8, 2.6, look.hair));
    px.rect(6, 9, 3, 6, look.hair).rect(23, 9, 3, 6, look.hair);
  }
  if (style === 'bald') {
    px.ellipse(8, 12.5, 2, 3, look.hair).ellipse(24, 12.5, 2, 3, look.hair);
    px.set(12, 6, shade(look.skin, 0.35)).set(13, 6, shade(look.skin, 0.35)).set(12, 7, shade(look.skin, 0.2));
  }
  if (style === 'cap') {
    px.ellipse(16, 8.5, 9.8, 6.4, look.accent, (_x, y) => y <= 8);
    px.rect(6, 8, 20, 2, shade(look.accent, -0.3)).rect(15, 4, 2, 2, '#f2c14e');
    px.rect(7, 10, 2, 4, look.hair).rect(23, 10, 2, 4, look.hair);
  }
  if (style === 'beanie') {
    px.ellipse(16, 8.5, 10.2, 7, look.accent, (_x, y) => y <= 9);
    px.rect(6, 8, 20, 3, shade(look.accent, -0.22)).ellipse(16, 1.8, 2.6, 2, shade(look.accent, 0.3));
    for (let x = 7; x <= 24; x += 3) px.set(x, 9, shade(look.accent, -0.38));
    px.rect(7, 11, 2, 3, look.hair).rect(23, 11, 2, 3, look.hair);
  }
  // лицо
  px.rect(10, 11, 4, 1, hairDark).rect(18, 11, 4, 1, hairDark);
  if (blink) {
    px.rect(10, 14, 3, 1, EYE).rect(19, 14, 3, 1, EYE);
  } else {
    px.rect(11, 13, 2, 3, EYE).rect(19, 13, 2, 3, EYE).set(11, 13, WHITE).set(19, 13, WHITE);
  }
  px.set(15, 16, skinDark).set(16, 17, skinDark);
  const blush = shade('#e88a8a', 0.15);
  px.rect(9, 17, 2, 1, blush).rect(21, 17, 2, 1, blush);
  if (has('beard')) {
    px.ellipse(16, 19.5, 7.6, 4.6, look.hair, (_x, y) => y >= 17).rect(12, 17, 8, 1, look.hair);
  }
  if (mouthOpen) px.rect(14, 19, 4, 2, '#4a1a22').rect(15, 20, 2, 1, '#d9606a');
  else px.rect(14, 19, 4, 1, '#8a3a3f');
  if (has('glasses')) {
    for (const x0 of [9, 17]) {
      px.rect(x0, 12, 6, 1, FRAME).rect(x0, 16, 6, 1, FRAME).rect(x0, 12, 1, 5, FRAME).rect(x0 + 5, 12, 1, 5, FRAME);
    }
    px.rect(15, 13, 2, 1, FRAME);
  }
  if (has('headset')) {
    px.ellipse(6.5, 14, 2, 3, FRAME).line(7, 17, 12, 20, FRAME).set(12, 20, '#6b6b78');
  }
  return px.outline(OUTLINE);
}

const portraitCache = new Map<string, HTMLCanvasElement>();

export function portraitSprite(look: CharLook, mouthOpen: boolean, blink: boolean): HTMLCanvasElement {
  const key = `${lookKey(look)}|${mouthOpen ? 1 : 0}|${blink ? 1 : 0}`;
  let cv = portraitCache.get(key);
  if (!cv) {
    cv = portraitPx(look, mouthOpen, blink).canvas();
    portraitCache.set(key, cv);
  }
  return cv;
}

// ---------------------------------------------------------------------------------------------- Граммозавр 48×48
export type BossPose = 'idle' | 'laugh' | 'hit' | 'down';

function bossPx(pose: BossPose, blink: boolean, breath: number): Px {
  const px = new Px(48, 48);
  const B = '#7c5cd6';
  const BD = '#5a3fb0';
  const BL = '#a58cf0';
  const BELLY = '#ece2ff';
  const MOUTH = '#3a1030';
  const dy = pose === 'down' ? 3 : breath;
  // хвост за телом
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    px.ellipse(29 + t * 16, 34 + t * 7 - Math.sin(t * Math.PI) * 5 + dy, 5.2 - t * 3.8, 4.6 - t * 3.2, B);
  }
  // лапы
  px.ellipse(17, 41, 4, 4.6, BD).ellipse(29, 41, 4, 4.6, BD);
  px.rect(12, 44, 9, 2, BD).rect(24, 44, 9, 2, BD);
  [12, 15, 24, 27].forEach((x) => px.set(x, 45, WHITE));
  // тело, живот, пятна
  px.ellipse(24, 31 + dy, 12.5, 11, B);
  px.ellipse(19, 24 + dy, 6, 3.4, BL, (x, y) => px.get(x, y) === B);
  px.ellipse(21, 33 + dy, 7, 8.4, BELLY);
  for (let y = 28; y <= 39; y += 3) px.ellipse(21, 33 + dy, 7, 8.4, '#d6c6fb', (_x, yy) => yy === y + dy);
  px.ellipse(31, 25 + dy, 2, 1.5, BD).ellipse(34, 31 + dy, 1.6, 1.2, BD).ellipse(30, 37 + dy, 1.6, 1.2, BD);
  // красная ручка учителя в лапе
  px.line(7, 20 + dy, 14, 30 + dy, '#e63946', 2).rect(6, 19 + dy, 2, 2, '#1d1d24');
  px.ellipse(14, 29 + dy, 3, 2.5, B);
  // голова и морда
  px.ellipse(20, 14 + dy, 11, 9, B);
  px.ellipse(11, 17 + dy, 8.5, 5.6, B);
  px.ellipse(18, 9 + dy, 5, 2.4, BL, (x, y) => px.get(x, y) === B);
  px.set(4, 15 + dy, BD).set(5, 15 + dy, BD);
  if (pose === 'laugh') {
    px.ellipse(10, 21 + dy, 7.5, 3.6, MOUTH);
    for (let x = 4; x <= 16; x += 3) px.set(x, 19 + dy, WHITE).set(x + 1, 19 + dy, WHITE);
    px.rect(8, 22 + dy, 4, 1, '#d9606a');
  } else {
    px.line(3, 20 + dy, 17, 21 + dy, MOUTH);
    for (let x = 5; x <= 15; x += 3) px.set(x, 21 + dy, WHITE);
  }
  // глаз и бровь
  if (blink || pose === 'down') {
    px.rect(19, 12 + dy, 5, 1, OUTLINE);
  } else if (pose === 'laugh') {
    px.line(19, 12 + dy, 21, 10 + dy, OUTLINE).line(21, 10 + dy, 23, 12 + dy, OUTLINE);
  } else {
    px.ellipse(21.5, 11.5 + dy, 3, 3, WHITE).rect(20, 10 + dy, 2, 3, OUTLINE).set(20, 10 + dy, WHITE);
  }
  px.line(17, 7 + dy, 25, 8 + dy, BD);
  // академическая шапочка с кисточкой
  const H = '#1d1b2e';
  px.rect(14, 4 + dy, 13, 3, H).rect(9, 2 + dy, 23, 2, H).rect(12, 1 + dy, 17, 1, H);
  px.line(31, 3 + dy, 33, 9 + dy, '#f2c14e').rect(32, 9 + dy, 2, 3, '#f2c14e');
  return px.outline(OUTLINE);
}

const bossCache = new Map<string, HTMLCanvasElement>();

export function bossSprite(pose: BossPose, blink: boolean, breath: number): HTMLCanvasElement {
  const key = `${pose}|${blink ? 1 : 0}|${breath}`;
  let cv = bossCache.get(key);
  if (!cv) {
    const base = pose === 'hit' ? bossPx('idle', false, breath).tint('#ffffff') : bossPx(pose, blink, breath);
    cv = base.canvas();
    bossCache.set(key, cv);
  }
  return cv;
}

// ---------------------------------------------------------------------------------------------- значки над домами
export type MarkerIcon = 'quest' | 'lock' | 'boss' | 'star' | 'done';

function markerPx(icon: MarkerIcon): Px {
  // поле 13×15: рисунок со сдвигом на пиксель, чтобы обводке было куда лечь со всех сторон
  const px = new Px(13, 15);
  const at = (x: number, y: number, w: number, h: number, c: string) => px.rect(x + 1, y + 1, w, h, c);
  if (icon === 'quest' || icon === 'done') {
    const c = icon === 'quest' ? '#f2c14e' : '#3dd68c';
    at(1, 0, 9, 10, c);
    at(4, 10, 3, 1, c);
    at(5, 11, 1, 1, c);
    if (icon === 'quest') {
      at(5, 2, 1, 4, OUTLINE);
      at(5, 7, 1, 1, OUTLINE);
    } else {
      px.line(4, 6, 5, 7, OUTLINE).line(6, 7, 8, 4, OUTLINE);
    }
  }
  if (icon === 'lock') {
    at(2, 5, 7, 6, '#e6b53b');
    at(3, 1, 1, 4, '#c9cbd3');
    at(7, 1, 1, 4, '#c9cbd3');
    at(4, 0, 3, 1, '#c9cbd3');
    at(5, 7, 1, 2, OUTLINE);
  }
  if (icon === 'boss') {
    px.ellipse(6.5, 6, 4.5, 4.5, '#a58cf0');
    at(3, 8, 5, 3, '#a58cf0');
    at(3, 4, 2, 2, OUTLINE);
    at(6, 4, 2, 2, OUTLINE);
    at(4, 9, 1, 1, OUTLINE);
    at(6, 9, 1, 1, OUTLINE);
  }
  if (icon === 'star') {
    at(5, 0, 1, 2, '#f36a1c');
    at(1, 3, 9, 2, '#f36a1c');
    at(3, 2, 5, 6, '#f36a1c');
    at(2, 7, 2, 2, '#f36a1c');
    at(7, 7, 2, 2, '#f36a1c');
  }
  return px.outline(OUTLINE);
}

const markerCache = new Map<MarkerIcon, HTMLCanvasElement>();

export function markerSprite(icon: MarkerIcon): HTMLCanvasElement {
  let cv = markerCache.get(icon);
  if (!cv) {
    cv = markerPx(icon).canvas();
    markerCache.set(icon, cv);
  }
  return cv;
}

/** «…» над говорящим персонажем. */
let bubble: HTMLCanvasElement | null = null;
export function bubbleSprite(): HTMLCanvasElement {
  if (!bubble) {
    const px = new Px(13, 10);
    px.rect(1, 1, 11, 5, WHITE).rect(3, 6, 2, 1, WHITE).set(3, 7, WHITE);
    px.set(3, 3, OUTLINE).set(6, 3, OUTLINE).set(9, 3, OUTLINE);
    bubble = px.outline(OUTLINE).canvas();
  }
  return bubble;
}
